import styled, { css } from 'styled-components'
import { desktopBreakpoint, mobileBreakpoint, tabletBreakpoint, wideScreenBreakpoint } from 'react-components'

export interface BackgroundImages {
    smallMobile?: string
    mobile?: string
    tablet?: string
    desktop?: string
    wideScreen?: string
}

// Breakpoint values from react-components:
// mobile: max: 767 (no min)
// tablet: min: 768, max: 1023
// desktop: min: 1024 (no max in original, but we cap at widescreen)
// wideScreen: min: 1276

export const Container = styled.section<{ backgroundImages: BackgroundImages; hasOverlay?: boolean }>`
    width: 100%;
    background-repeat: no-repeat;
    background-position: top center;
    background-size: 100% 67%;

    /* Small Mobile (0 - 479px) */
    @media only screen and (max-width: 479px) {
        ${({ backgroundImages, hasOverlay }) =>
            backgroundImages.smallMobile &&
            css`
                background-image: ${hasOverlay
                    ? `linear-gradient(rgba(33, 33, 33, 0.4), rgba(33, 33, 33, 0.4)), linear-gradient(to bottom, transparent 0%, transparent 75%, rgba(255, 255, 255, 0.8) 100%), url('${backgroundImages.smallMobile}')`
                    : `linear-gradient(to bottom, transparent 0%, transparent 75%, rgba(255, 255, 255, 0.8) 100%), url('${backgroundImages.smallMobile}')`};
            `}
    }

    /* Mobile (480px - 767px) */
    @media only screen and (min-width: 480px) and (max-width: ${mobileBreakpoint.max}px) {
        ${({ backgroundImages, hasOverlay }) =>
            backgroundImages.mobile &&
            css`
                background-image: ${hasOverlay
                    ? `linear-gradient(rgba(33, 33, 33, 0.4), rgba(33, 33, 33, 0.4)), linear-gradient(to bottom, transparent 0%, transparent 75%, rgba(255, 255, 255, 0.8) 100%), url('${backgroundImages.mobile}')`
                    : `linear-gradient(to bottom, transparent 0%, transparent 75%, rgba(255, 255, 255, 0.8) 100%), url('${backgroundImages.mobile}')`};
            `}
    }

    /* Tablet (768px - 1023px) */
    @media only screen and (min-width: ${tabletBreakpoint.min}px) and (max-width: ${tabletBreakpoint.max}px) {
        background-size: 100% 100%;
        height: 22rem;

        ${({ backgroundImages, hasOverlay }) =>
            backgroundImages.tablet &&
            css`
                background-image: ${hasOverlay
                    ? `linear-gradient(rgba(33, 33, 33, 0.4), rgba(33, 33, 33, 0.4)), url('${backgroundImages.tablet}')`
                    : `url('${backgroundImages.tablet}')`};
            `}
    }

    /* Desktop (1024px - 1275px) */
    @media only screen and (min-width: ${desktopBreakpoint.min}px) and (max-width: ${(wideScreenBreakpoint?.min ??
            1276) - 1}px) {
        background-size: 100% 100%;
        height: 22.5rem;

        ${({ backgroundImages, hasOverlay }) =>
            backgroundImages.desktop &&
            css`
                background-image: ${hasOverlay
                    ? `linear-gradient(rgba(33, 33, 33, 0.4), rgba(33, 33, 33, 0.4)), url('${backgroundImages.desktop}')`
                    : `url('${backgroundImages.desktop}')`};
            `}
    }

    /* Wide Screen (1276px and above) */
    @media only screen and (min-width: ${wideScreenBreakpoint.min}px) {
        background-size: 100% 100%;
        height: 22.5rem;

        ${({ backgroundImages, hasOverlay }) =>
            backgroundImages.wideScreen &&
            css`
                background-image: ${hasOverlay
                    ? `linear-gradient(rgba(33, 33, 33, 0.4), rgba(33, 33, 33, 0.4)), url('${backgroundImages.wideScreen}')`
                    : `url('${backgroundImages.wideScreen}')`};
            `}
    }
`

export const ContentWrapper = styled.div`
    position: relative;
    display: flex;
    flex: 1;
    flex-direction: column;
    width: 100%;
    align-items: center; /* default desktop */
    padding: 0 1rem;
    line-height: 1.6;

    @media only screen and (max-width: ${mobileBreakpoint.max}px) {
        align-items: center;
    }

    @media only screen and (min-width: ${tabletBreakpoint.min}px) {
        top: 1.875rem;
        padding: 0.625rem 1rem;
        bottom: 3rem;
        justify-content: ${({ theme }) => theme.style.justifyContent.center};
    }

    @media only screen and (min-width: ${desktopBreakpoint.min}px) {
        top: 4rem;
    }
`

export const SubtitleWrapper = styled.span<{ textColor?: string }>`
    display: block;
    text-align: center;
    width: 100%;

    font-family: ${({ theme }) => theme.typography.font.family.lato};
    font-size: 1rem;
    color: ${({ theme, textColor }) => textColor || theme.colors.tfwBlack87};

    line-height: 1.5rem;
    margin-bottom: ${({ theme }) => theme.spacing.large};

    @media (min-width: ${tabletBreakpoint.min}px) {
        padding: 0 1.5rem;
        font-feature-settings: 'ordn' on;
    }

    @media (min-width: ${desktopBreakpoint.min}px) {
        max-width: 61.875rem;
        margin: 0 1.875rem 1rem;
        letter-spacing: -0.021875rem;
    }

    @media (max-width: ${mobileBreakpoint.max}px) {
        /* Ensure center alignment on mobile too */
        text-align: center;
    }
`

export const TitleWrapper = styled.h1<{ textColor?: string }>`
    display: block;
    text-align: center;
    width: 100%;

    margin-top: ${({ theme }) => theme.spacing.xLarge};
    margin-bottom: ${({ theme }) => theme.spacing.xSmall};

    color: ${({ theme, textColor }) => textColor || theme.colors.tfwBlack87};
    font-family: ${({ theme }) => theme.typography.font.family.lato};

    font-size: 2rem;
    font-weight: 600;
    line-height: 1.875rem;

    @media (min-width: ${tabletBreakpoint.min}px) {
        letter-spacing: -0.05rem;
        width: 100%;
        margin: ${({ theme }) => theme.spacing.large} 3.375rem 0.5rem;
        font-size: 2rem;
        line-height: 2.5rem;
    }

    @media (min-width: ${desktopBreakpoint.min}px) {
        max-width: 61.875rem;
        margin: 0 1.875rem 0.5rem;
        letter-spacing: -0.03125rem;
    }

    @media (max-width: ${mobileBreakpoint.max}px) {
        text-align: center;
        font-size: 1.25rem;
    }
`

export const ViewToggleContainer = styled.div`
    display: flex;
    justify-content: center;
    gap: 0.25rem;
    padding: 0.5rem;
    background: ${({ theme }) => `
        linear-gradient(
            0deg,
            ${theme.colors.white} 0%,
            rgba(245,245,245,0.5) 100%
        )
    `};
    border-radius: 1rem 1rem 0 0;
    @media (max-width: ${mobileBreakpoint.max}px) {
        width: 100%;
        margin-bottom: -0.875rem;
    }
`

const activeShadow = `
    0px 1px 5px #00000014,
    0px 2px 1px #0000000A,
    0px 1px 2px #0000000F
`

const toggleBaseStyles = css<{ isActive: boolean }>`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem 0.5rem 0.5rem;
    border-radius: 0.75rem;
    border: none;
    margin: 0.125rem;
    cursor: pointer;
    line-height: 1.375rem;
    color: ${({ theme }) => theme.colors.tfwBlack99};
    background: ${({ isActive }) => (isActive ? 'var(--colour-background-subtle-neutral-1, #FFFFFF)' : 'transparent')};
    box-shadow: ${({ isActive }) => (isActive ? activeShadow : 'none')};
    transition: background 0.2s ease, box-shadow 0.2s ease;
    &:hover {
        background: ${({ isActive }) =>
            isActive ? 'var(--colour-background-subtle-neutral-1, #FFFFFF)' : 'rgba(0,0,0,0.05)'};
    }

    @media (max-width: ${mobileBreakpoint.max}px) {
        margin-bottom: 1rem;
        width: 100%;
    }
`

export const ToggleButton = styled.button<{ isActive: boolean }>`
    ${toggleBaseStyles}
`

export const ToggleAnchor = styled.a<{ isActive: boolean }>`
    ${toggleBaseStyles}
    text-decoration: none;
    pointer-events: ${({ isActive }) => (isActive ? 'none' : 'auto')};
`

export const Icon = styled.img`
    width: 2rem;
    height: 2rem;
`

export const TabText = styled.span`
    font-weight: 400;
    font-size: 1rem;
    text-align: left;
`
